import { getToken } from './auth';

const API_URL = process.env.NEXT_PUBLIC_API_URL || 'http://localhost:3000';

export interface ApiResponse<T> {
  status: string;
  code: number;
  message: string;
  meta?: {
    total_records?: number;
    current_page?: number;
    page_size?: number;
    total_pages?: number;
    sort_by?: string;
    sort_dir?: string;
  };
  data: T;
}

class ApiClient {
  private baseUrl: string;

  constructor(baseUrl: string = API_URL) {
    this.baseUrl = baseUrl;
  }

  private getHeaders(): HeadersInit {
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
    };

    const token = getToken();
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    return headers;
  }

  async get<T>(endpoint: string, params?: Record<string, any>): Promise<ApiResponse<T>> {
    const url = new URL(`${this.baseUrl}${endpoint}`);
    
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          url.searchParams.append(key, String(value));
        }
      });
    }

    try {
      const response = await fetch(url.toString(), {
        method: 'GET',
        headers: this.getHeaders(),
      });

      return this.handleResponse<T>(response);
    } catch (error) {
      console.error('API GET request failed:', error);
      throw new Error(`Failed to fetch from ${endpoint}: ${error instanceof Error ? error.message : String(error)}`);
    }
  }

  async post<T>(endpoint: string, data?: any): Promise<ApiResponse<T>> {
    try {
      const response = await fetch(`${this.baseUrl}${endpoint}`, {
        method: 'POST',
        headers: this.getHeaders(),
        body: data ? JSON.stringify(data) : undefined,
      });

      return this.handleResponse<T>(response);
    } catch (error) {
      console.error('API POST request failed:', error);
      throw new Error(`Failed to POST to ${endpoint}: ${error instanceof Error ? error.message : String(error)}`);
    }
  }

  async put<T>(endpoint: string, data?: any): Promise<ApiResponse<T>> {
    try {
      const response = await fetch(`${this.baseUrl}${endpoint}`, {
        method: 'PUT',
        headers: this.getHeaders(),
        body: data ? JSON.stringify(data) : undefined,
      });

      return this.handleResponse<T>(response);
    } catch (error) {
      console.error('API PUT request failed:', error);
      throw new Error(`Failed to PUT to ${endpoint}: ${error instanceof Error ? error.message : String(error)}`);
    }
  }

  async patch<T>(endpoint: string, data?: any): Promise<ApiResponse<T>> {
    try {
      const response = await fetch(`${this.baseUrl}${endpoint}`, {
        method: 'PATCH',
        headers: this.getHeaders(),
        body: data ? JSON.stringify(data) : undefined,
      });

      return this.handleResponse<T>(response);
    } catch (error) {
      console.error('API PATCH request failed:', error);
      throw new Error(`Failed to PATCH to ${endpoint}: ${error instanceof Error ? error.message : String(error)}`);
    }
  }

  async delete<T>(endpoint: string): Promise<ApiResponse<T>> {
    try {
      const response = await fetch(`${this.baseUrl}${endpoint}`, {
        method: 'DELETE',
        headers: this.getHeaders(),
      });

      return this.handleResponse<T>(response);
    } catch (error) {
      console.error('API DELETE request failed:', error);
      throw new Error(`Failed to DELETE ${endpoint}: ${error instanceof Error ? error.message : String(error)}`);
    }
  }

  private async handleResponse<T>(response: Response): Promise<ApiResponse<T>> {
    if (!response.ok) {
      const error = await response.json().catch(() => ({
        status: 'error',
        code: response.status,
        message: response.statusText,
        data: null,
      }));
      throw new Error(error.message || `HTTP Error: ${response.status}`);
    }

    // Handle 204 No Content responses
    if (response.status === 204) {
      return {
        status: 'success',
        code: 204,
        message: 'Resource deleted successfully',
        data: null as any,
      };
    }

    const data = await response.json();
    return data as ApiResponse<T>;
  }
}

export const apiClient = new ApiClient();
